var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/index.ts
import {
  Upload as TusUpload
} from "tus-js-client";
import resolveUrl from "@availity/resolve-url";

// src/http-stack.ts
var Response = class {
  constructor(response) {
    this._body = "";
    this._response = response;
  }
  getStatus() {
    return this._response.status;
  }
  getHeader(header) {
    return this._response.headers.get(header) || void 0;
  }
  getBody() {
    return this._body;
  }
  parseBody() {
    return __async(this, null, function* () {
      this._body = yield this._response.text();
    });
  }
  getUnderlyingObject() {
    return this._response;
  }
};
var Request = class {
  constructor(method, url) {
    this._method = method;
    this._url = url;
    this._headers = {};
    this._abortController = new AbortController();
  }
  getMethod() {
    return this._method;
  }
  getURL() {
    return this._url;
  }
  setHeader(header, value) {
    this._headers[header] = value;
  }
  getHeader(header) {
    return this._headers[header];
  }
  setProgressHandler(progressHandler) {
    this._progressHandler = progressHandler;
  }
  send(body = null) {
    return __async(this, null, function* () {
      var _a;
      try {
        const response = yield fetch(this._url, {
          method: this._method,
          headers: this._headers,
          body,
          signal: this._abortController.signal,
          credentials: "include"
          // Add duplex: 'half' if you need to stream request body
        });
        if (this._progressHandler) {
          const reader = (_a = response.clone().body) == null ? void 0 : _a.getReader();
          if (reader) {
            let loaded = 0;
            let done;
            let value;
            while (!done) {
              ({ value, done } = yield reader.read());
              if (done) {
                break;
              }
              loaded += (value == null ? void 0 : value.length) || 0;
              this._progressHandler(loaded);
            }
          }
        }
        const _response = new Response(response.clone());
        _response.parseBody();
        return _response;
      } catch (error) {
        if (error.name === "AbortError") {
          throw new Error("Request aborted");
        }
        throw error;
      }
    });
  }
  abort() {
    this._abortController.abort();
    return Promise.resolve();
  }
  getUnderlyingObject() {
    return this._abortController;
  }
};
var FetchHttpStack = class {
  createRequest(method, url) {
    return new Request(method, url);
  }
  getName() {
    return "FetchHttpStack";
  }
};

// src/util.ts
var hashCode = (str) => {
  let hash = 0;
  if (str.length === 0) return hash;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return hash;
};
var isDetailedError = (error) => error.originalResponse !== void 0;
var createFingerprint = (file, options, callback) => new Promise((resolve) => {
  const { metadata = {}, endpoint } = options;
  const values = Object.values(metadata);
  const attributes = [file.name, file.type, file.size, file.lastModified];
  const signature = [attributes.toString().replaceAll(",", ""), endpoint, values].join("");
  const hash = Math.abs(hashCode(signature));
  const fingerprint = ["tus", ...attributes, hash].filter((value) => value != null ? value : false).join("-");
  if (callback) {
    resolve(callback(null, fingerprint));
  } else {
    resolve(fingerprint);
  }
});

// src/index.ts
var defaultOptions = {
  endpoint: "/ms/api/availity/internal/core/vault/upload/v1/resumable",
  chunkSize: 6e6,
  // 6MB (Max size from backend is 5MiB. Setting to 6MB to be safe)
  removeFingerprintOnSuccess: true,
  pollingTime: 5e3,
  retryDelays: [0, 1e3, 3e3, 5e3],
  stripFileNamePathSegments: true,
  onPreStart: [],
  maxAvScanRetries: 10,
  fingerprint: createFingerprint
};
var Upload = class {
  constructor(file, options) {
    if (!file) {
      throw new Error("[file] must be defined and of type File");
    }
    if (!options || !options.bucketId) {
      throw new Error("[options.bucketId] must be defined");
    }
    if (!options.customerId) {
      throw new Error("[options.customerId] must be defined");
    }
    if (!options.clientId) {
      throw new Error("[options.clientId] must be defined");
    }
    this.file = file;
    this.options = __spreadValues(__spreadValues({}, defaultOptions), options);
    this.options.endpoint = resolveUrl({ relative: this.options.endpoint });
    this.avScanRetries = 0;
    this.bytesAccepted = 0;
    this.bytesScanned = 0;
    this.bytesSent = 0;
    this.bytesTotal = 0;
    this.chunkSize = 0;
    this.error = null;
    this.errorMessage = "";
    this.id = "";
    this.onError = [];
    this.onPreStart = this.options.onPreStart || [];
    this.onProgress = [];
    this.onSuccess = [];
    this.onChunkComplete = [];
    this.percentage = 0;
    this.preStartValidationResults = [];
    this.references = [];
    this.s3References = [];
    this.status = "pending";
    this.timeoutId = void 0;
    this.waitForPassword = true;
    const fileName = this.trimFileName(file.name);
    const metadata = {
      "availity-filename": fileName,
      "availity-content-type": file.type,
      "availity-attachment-name": "N/A"
    };
    Object.assign(metadata, this.options.metadata);
    const upload = new TusUpload(this.file, {
      endpoint: `${this.options.endpoint}/${this.options.bucketId}/`,
      chunkSize: this.options.chunkSize,
      retryDelays: this.options.retryDelays,
      removeFingerprintOnSuccess: this.options.removeFingerprintOnSuccess,
      fingerprint: this.options.fingerprint,
      metadata,
      headers: {
        "X-XSRF-TOKEN": this.getToken(),
        "X-Availity-Customer-ID": this.options.customerId,
        "X-Client-ID": this.options.clientId
      },
      httpStack: new FetchHttpStack(),
      onError: (error) => {
        this.setError("rejected", "Network Error", error);
        this.error = error;
      },
      onProgress: (bytesSent, bytesTotal) => {
        this.bytesSent = bytesSent;
        this.bytesTotal = bytesTotal;
        this.percentage = this.getPercentage();
        for (const handleOnProgress of this.onProgress) {
          handleOnProgress();
        }
      },
      onChunkComplete: (chunkSize, bytesAccepted, bytesTotal) => {
        this.chunkSize = chunkSize;
        this.bytesAccepted = bytesAccepted;
        this.bytesTotal = bytesTotal;
        for (const handleOnChunkComplete of this.onChunkComplete) {
          handleOnChunkComplete(chunkSize, bytesAccepted, bytesTotal);
        }
      },
      onSuccess: (response) => {
        this.bytesScanned = Number.parseInt(response.lastResponse.getHeader("AV-Scan-Bytes") || "0", 10);
        this.percentage = this.getPercentage();
        const result = this.getResult(response.lastResponse);
        if (result.status === "accepted") {
          this.percentage = 100;
          this.status = result.status;
          this.errorMessage = "";
          this.error = null;
          const references = response.lastResponse.getHeader("references");
          const s3References = response.lastResponse.getHeader("s3-references");
          if (references) {
            this.references = JSON.parse(references);
          }
          if (s3References) {
            this.s3References = JSON.parse(s3References);
          }
          for (const handleOnSuccess of this.onSuccess) {
            handleOnSuccess(response);
          }
          return;
        }
        if (result.status === "rejected") {
          this.setError(result.status, result.message);
          return;
        }
        this.scan();
      }
    });
    this.upload = upload;
  }
  inStatusCategory(status, category) {
    return status >= category && status < category + 100;
  }
  scan(data) {
    return __async(this, null, function* () {
      clearTimeout(this.timeoutId);
      try {
        const http = new FetchHttpStack();
        const request = http.createRequest("HEAD", this.upload.url || this.options.endpoint);
        const headers = {
          "Tus-Resumable": "1.0.0",
          "X-Client-ID": this.options.clientId,
          "X-Availity-Customer-ID": this.options.customerId,
          "X-XSRF-TOKEN": this.getToken()
        };
        if (data) {
          headers[data.header] = data.value;
        }
        for (const [key, value] of Object.entries(headers)) {
          request.setHeader(key, value);
        }
        const response = yield request.send();
        if (!this.inStatusCategory(response.getStatus(), 200)) {
          this.setError("rejected", `Invalid status returned: ${response.getStatus()}`);
          return;
        }
        this.bytesScanned = Number.parseInt(response.getHeader("AV-Scan-Bytes") || "0", 10);
        this.percentage = this.getPercentage();
        const result = this.getResult(response);
        if (result.status === "rejected") {
          this.setError(result.status, result.message);
          return;
        }
        if (result.status === "encrypted" && this.waitForPassword) {
          this.setError(result.status, result.message);
          clearTimeout(this.timeoutId);
          return;
        }
        if (result.status === "accepted") {
          this.percentage = 100;
          this.status = result.status;
          this.errorMessage = "";
          this.error = null;
          const references = response.getHeader("references");
          const s3References = response.getHeader("s3-references");
          if (references) {
            this.references = JSON.parse(references);
          }
          if (s3References) {
            this.s3References = JSON.parse(s3References);
          }
          for (const handleOnSuccess of this.onSuccess) {
            handleOnSuccess({ lastResponse: response });
          }
          return;
        }
        if (result.status === "decrypting") {
          this.setError(result.status, result.message);
        }
        for (const handleOnProgress of this.onProgress) {
          handleOnProgress();
        }
      } catch (error) {
        this.setError("rejected", "Network Error", error);
        this.error = error;
      }
      if (this.avScanRetries > this.options.maxAvScanRetries) {
        this.setError("rejected", "AV scan timed out, max retries exceeded");
        return;
      }
      this.timeoutId = setTimeout(() => {
        this.avScanRetries += 1;
        this.scan();
      }, this.options.pollingTime);
    });
  }
  getPercentage() {
    if (!this.bytesTotal) return 0;
    const processedBytes = this.bytesSent + this.bytesScanned;
    const combinedTotalBytes = this.bytesTotal * 2;
    return processedBytes / combinedTotalBytes * 100;
  }
  getToken() {
    return document.cookie.replace(/(?:(?:^|.*;\s*)XSRF-TOKEN\s*=\s*([^;]*).*$)|^.*$/, "$1");
  }
  start() {
    if (!this.isValidFile()) {
      return;
    }
    if (!this.id) {
      this.setError(
        "rejected",
        "No id set. Call `generateId` or set one manually with `setId` before calling `start`."
      );
      return;
    }
    for (const handleOnPreStart of this.onPreStart) {
      this.preStartValidationResults.push(handleOnPreStart(this));
    }
    if (this.preStartValidationResults.some((result) => !result)) {
      if (this.status === "pending") {
        this.setError("rejected", "preStart validation failed");
      }
      return;
    }
    this.upload.start();
  }
  setId(id) {
    this.id = id;
  }
  generateId() {
    return __async(this, null, function* () {
      const { fingerprint } = this.options;
      const id = yield fingerprint(this.file, this.options);
      this.setId(id.replaceAll(/[^\dA-Za-z-]/g, ""));
      return this.id;
    });
  }
  fingerprint(file, options, callback) {
    return __async(this, null, function* () {
      return createFingerprint(file, options, callback);
    });
  }
  sendPassword(pw) {
    this.waitForPassword = false;
    this.scan({ header: "Encryption-Password", value: pw });
  }
  isValidSize() {
    if (this.options.maxSize && this.file.size > this.options.maxSize) {
      this.setError("rejected", "Document is too large");
      return false;
    }
    return true;
  }
  isAllowedFileTypes() {
    if (!this.options.fileTypes || this.options.fileTypes.length === 0) return true;
    if (!this.file.name) return false;
    const fileName = this.file.name;
    const fileExt = fileName.substring(fileName.lastIndexOf(".")).toLowerCase();
    this.options.fileTypes = this.options.fileTypes.map((type) => type.toLowerCase());
    if (!this.options.fileTypes.includes(fileExt)) {
      this.setError("rejected", `Document type ${fileExt} is not allowed`);
      return false;
    }
    return true;
  }
  isAllowedFileNameCharacters() {
    if (!this.options.allowedFileNameCharacters) return true;
    const fileName = this.file.name.substring(0, this.file.name.lastIndexOf("."));
    const regExp = new RegExp(`([^${this.options.allowedFileNameCharacters}])`, "g");
    if (fileName.match(regExp) !== null) {
      this.setError("rejected", "File name contains characters not allowed");
      return false;
    }
    return true;
  }
  isValidFile() {
    return this.isAllowedFileNameCharacters() && this.isAllowedFileTypes() && this.isValidSize();
  }
  trimFileName(fileName) {
    if (this.options.stripFileNamePathSegments) {
      fileName = fileName.substring(fileName.lastIndexOf("\\") + 1);
      fileName = fileName.substring(fileName.lastIndexOf("/") + 1);
    }
    return fileName;
  }
  getResult(response) {
    const scanResult = response.getHeader("av-scan-result");
    const uploadResult = response.getHeader("upload-result");
    const decryptResult = response.getHeader("decryption-result");
    const msg = response.getHeader("upload-message");
    if (scanResult === "rejected") {
      return { status: scanResult, message: msg || "Failed AV scan" };
    }
    if (uploadResult === "rejected") {
      this.waitForPassword = true;
      if (decryptResult === "rejected") {
        return {
          status: uploadResult,
          message: msg || "Maximum password attempts reached"
        };
      }
      return { status: uploadResult, message: msg || "File upload rejected" };
    }
    if (uploadResult === "encrypted") {
      if (!this.waitForPassword && (decryptResult === null || decryptResult === "pending")) {
        return { status: "decrypting", message: msg || "Decrypting file" };
      }
      if (decryptResult === "rejected") {
        this.waitForPassword = true;
        return { status: uploadResult, message: msg || "Incorrect password" };
      }
      return {
        status: uploadResult,
        message: msg || "Encrypted files require a password"
      };
    }
    if (scanResult === "accepted" && uploadResult === "accepted") {
      return { status: "accepted", message: msg || "" };
    }
    return { status: "pending", message: msg || "" };
  }
  setError(status, message, error) {
    this.status = status;
    try {
      this.parseErrorMessage(message, error);
    } catch (e) {
    }
    for (const handleOnError of this.onError) {
      handleOnError(error || new Error(this.errorMessage));
    }
  }
  parseErrorMessage(message, error) {
    if (error) {
      let msg = "";
      if (isDetailedError(error)) {
        msg = error.originalRequest.getHeader("Upload-Message") || "";
      }
      if (!msg) {
        msg = error.message;
      }
      if (msg) {
        this.errorMessage = msg;
        return;
      }
    }
    this.errorMessage = message;
  }
  abort() {
    if (this.upload) {
      this.upload.abort();
    }
  }
};
var index_default = Upload;
export {
  index_default as default
};
